import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, finalize } from 'rxjs';
import { StageGatePrompt, StageGateService } from '../../services/stage-gate.service';
import { StageItem, stageItemShown } from '../../models/stage-requirement.model';

/** The "before you move this card" pop-up - see StageGateService. Mounted
 *  once in the app shell so every stage change (board, list, record page,
 *  bulk move) goes through it. */
@Component({
  selector: 'app-stage-gate-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './stage-gate-modal.component.html',
  styleUrl: './stage-gate-modal.component.css'
})
export class StageGateModalComponent implements OnInit, OnDestroy {
  prompt: StageGatePrompt | null = null;
  answers: Record<string, any> = {};
  errors: Record<string, string> = {};
  error = '';
  saving = false;
  private sub?: Subscription;

  constructor (private gate: StageGateService) {}

  ngOnInit (): void {
    this.sub = this.gate.prompt$.subscribe(p => {
      this.prompt = p;
      this.errors = {};
      this.error = '';
      this.answers = {};
      for (const i of p?.requirement.items ?? []) {
        this.answers[i.key] = i.type === 'checklist' || i.type === 'checkboxes' ? [] : '';
      }
    });
  }

  ngOnDestroy (): void { this.sub?.unsubscribe(); }

  shown (i: StageItem): boolean { return stageItemShown(i, this.answers); }

  toggle (key: string, value: string, on: boolean): void {
    const list: string[] = this.answers[key] ?? [];
    this.answers[key] = on ? [...list, value] : list.filter(v => v !== value);
    delete this.errors[key];
  }

  cancel (): void {
    if (this.prompt && !this.saving) this.gate.close(this.prompt, false);
  }

  submit (): void {
    const p = this.prompt;
    if (!p || this.saving) return;
    this.errors = {};
    for (const i of p.requirement.items) {
      if (i.type === 'info' || !i.required || !this.shown(i)) continue;
      const v = this.answers[i.key];
      if (i.type === 'checklist' ? (v?.length ?? 0) < (i.checklist?.length ?? 0) : (v === '' || v === null || (Array.isArray(v) && !v.length))) {
        this.errors[i.key] = i.type === 'checklist' ? 'Tick every item' : 'Required';
      }
    }
    if (Object.keys(this.errors).length) { this.error = 'Please answer the required questions.'; return; }

    this.saving = true;
    this.error = '';
    this.gate.submit(p, this.answers).pipe(finalize(() => this.saving = false)).subscribe({
      next: () => this.gate.close(p, true),
      error: (err: any) => {
        const fieldErrors = err?.error?.errors ?? {};
        Object.keys(fieldErrors).forEach(k => this.errors[k] = fieldErrors[k]?.[0]);
        this.error = err?.error?.message || 'Could not save the answers - the card was not moved.';
      }
    });
  }
}
